import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "Salary Components", path: "/salary-v2/components", desc: "View and configure earning and deduction components" },
  { label: "Formula Builder", path: "/salary-v2/formula-builder", desc: "Write and test component formulas" },
  { label: "Derived Variables", path: "/salary-v2/derived-variables", desc: "Manage named formula variables like MONTHLY_CTC" },
  { label: "Salary Structures", path: "/salary-v2/structures", desc: "Create and manage salary templates" },
  { label: "Employee Salary Structure", path: "/employeesalarystructure", desc: "Assign templates and CTC to employees" },
  { label: "Dependency Graph", path: "/salary-v2/dependencies", desc: "Visualise component dependencies and detect cycles" },
  { label: "Salary Simulator", path: "/salary-v2/simulator", desc: "Preview salary breakdown for a given CTC" },
  { label: "Statutory Settings", path: "/settings/statutory/pf", desc: "Configure PF, ESI, PT, and LWF settings" },
  { label: "Payroll Setup Wizard", path: "/salary-v2/setup", desc: "Guided 5-step payroll configuration" },
  { label: "CTC Planner", path: "/tools/ctc-planner", desc: "Break down Annual CTC into component totals" },
];

const SALARY_TROUBLESHOOTING = [
  {
    problem: "Salary component not appearing in the salary template",
    check: "A component must be created in Salary Components before it can be added to a salary template. If the component exists but does not appear when adding components to a template, verify that the component's category (earning, deduction, benefit, employer contribution, or statutory) matches what you are looking for.",
    action: "Go to Salary Components and confirm the component exists and is active. Then go to Salary Structures, open the template, and add the component. If the component is missing from the list, create it first.",
    path: "/salary-v2/components",
    pathLabel: "Salary Components",
  },
  {
    problem: "Salary template shows incorrect component amounts",
    check: "Each component in a template has a calculation source: fixed amount, percentage, component formula, derived variable, statutory engine, or component reference. If the amounts look wrong, the source configuration for that component may be incorrect.",
    action: "Open the salary template in Salary Structures. Click on the component to check its calculation source and value. If it uses a formula, verify the expression in the Formula Builder. If it uses a derived variable, check the variable in Derived Variables.",
    path: "/salary-v2/structures",
    pathLabel: "Salary Structures",
  },
  {
    problem: "Formula component returning unexpected results",
    check: "Formula components compute values from other variables using expressions. Common issues include referencing a variable that evaluates to zero, incorrect operator precedence (missing parentheses), or using the wrong variable name. Available variables include BASIC, HRA, GROSS, CTC, MONTHLY_CTC, ANNUAL_CTC, WORKING_DAYS, PAYABLE_DAYS, LOP_DAYS, and others.",
    action: "Open the Formula Builder and review the formula expression. Use the Salary Simulator to test the formula with a sample CTC. Check that all referenced variables have valid values. Supported functions are: min, max, round, abs, floor, ceil, sqrt.",
    path: "/salary-v2/formula-builder",
    pathLabel: "Formula Builder",
  },
  {
    problem: "Circular dependency detected — payroll cannot run",
    check: "This error means component formulas reference each other in a loop (e.g. Component A depends on Component B, and Component B depends on Component A). The system cannot resolve circular references.",
    action: "Open the Dependency Graph to visualise the cycle and identify which components are involved. Then open the Formula Builder and rewrite one of the circular formulas to break the loop.",
    path: "/salary-v2/dependencies",
    pathLabel: "Dependency Graph",
  },
  {
    problem: "Employee salary structure not assigned — employee missing from payroll",
    check: "For an employee to appear in payroll, they must have a salary template assigned with an Annual CTC and an effective date. If no structure is assigned, the employee will not be included in payroll processing.",
    action: "Go to Employee Salary Structure. Select the employee, choose an active salary template, enter the Annual CTC, set the effective date, and save. For bulk assignment, use Step 5 (Employee Allocation) of the Payroll Setup Wizard.",
    path: "/employeesalarystructure",
    pathLabel: "Employee Salary Structure",
  },
  {
    problem: "Cannot delete a salary template — 'template may be assigned to employees'",
    check: "A salary template cannot be deleted while it is assigned to one or more employees. The system prevents deletion to avoid breaking existing salary configurations.",
    action: "Go to Employee Salary Structure and identify which employees are using this template. Reassign them to a different template, then return to Salary Structures to delete the original.",
    path: "/employeesalarystructure",
    pathLabel: "Employee Salary Structure",
  },
  {
    problem: "Salary Simulator returns 'Simulation failed'",
    check: "The simulator could not compute results. This usually happens when the selected template has components with missing formulas, invalid expressions, or circular dependencies.",
    action: "Select the template in the Salary Simulator and note which step fails. Then check the template's components in Salary Structures for missing or invalid formulas. Use the Dependency Graph to rule out circular references.",
    path: "/salary-v2/simulator",
    pathLabel: "Salary Simulator",
  },
  {
    problem: "Statutory deductions (PF/ESI/PT) not calculating correctly",
    check: "Statutory components are calculated by the system engine based on your Statutory Settings configuration. PF depends on the wage ceiling and contribution rates. ESI depends on the gross salary threshold. Professional Tax is state-specific. If these are not configured, the system uses defaults.",
    action: "Go to Statutory Settings and verify PF wage limit, contribution rates, ESI applicability, and PT state rules. Also check that the employee has the correct statutory flags (PF member, ESI applicable, PT applicable) set in their profile.",
    path: "/settings/statutory/pf",
    pathLabel: "Statutory Settings",
  },
  {
    problem: "Derived variable returning zero or unexpected value",
    check: "Derived variables are named formulas that compute values from other variables. Standard presets include MONTHLY_CTC (Annual CTC / 12), BASIC (MONTHLY_CTC * 0.40), HRA (BASIC * 0.40), etc. A derived variable will return zero if any variable it references is zero or undefined.",
    action: "Go to Derived Variables and review the expression for the variable in question. Verify that all variables it references have been defined and have non-zero values. Test the calculation using the Salary Simulator with a sample CTC.",
    path: "/salary-v2/derived-variables",
    pathLabel: "Derived Variables",
  },
  {
    problem: "Component groups not organising components as expected",
    check: "Component groups let you organise related salary components together (e.g. a PF_GROUP). Each group has a code, name, and description. Items within the group have a sequence number for ordering.",
    action: "Go to Component Groups and verify the group exists with the correct code. Check that the expected components are added to the group and that the sequence numbers reflect the desired order.",
    path: "/salary-v2/groups",
    pathLabel: "Component Groups",
  },
  {
    problem: "Attendance proration not applied to salary component",
    check: "Salary components can be configured with an attendance proration mode. When enabled, the component amount is prorated based on payable days vs. working days. If proration is not set on the component, it will pay the full amount regardless of attendance.",
    action: "Go to Salary Components and open the component. Check if attendance proration is enabled. If you need the component to be prorated for partial months or LOP, enable the proration setting and select the appropriate mode.",
    path: "/salary-v2/components",
    pathLabel: "Salary Components",
  },
];

export default function SalarySupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return SALARY_TROUBLESHOOTING;
    return SALARY_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-rose">
              <IndianRupee size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Salary Component</h1>
              <p className="hs-hero-sub">
                Troubleshoot salary structure and component configuration issues.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search salary issues — e.g. formula, template, statutory..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Salary Component Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Salary Component Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No salary issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed salary information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed salary information?</h2>
              <p className="sc-info-row-desc">
                Visit the Salary &amp; Components Help Guide for detailed documentation, FAQs and step-by-step salary configuration instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/salary")}
            >
              Open Salary Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
